import React from 'react';
import ContactForm from './ContactForm.js';
import '../styles/tailwind.css';
import '../styles/ContactStyles.css';
import { Helmet } from 'react-helmet-async';
import AnimateOnScroll from './AnimateOnScroll.js';
import contactHero from '../assets/contact-hero.jpg';
import avettalogo from '../assets/Avetta-Logo.png';
import ivmalogo from '../assets/ivmaofbc124x59_2x.png';
import worksafebclogo from '../assets/worksafebcstamp.jpg';
import isclogo from '../assets/ISClogo.png';

const ContactPage = () => {
  return (
    <>
      <Helmet>
        <title>Contact Boultbee | Get a Free Quote | BC-Wide</title>
        <meta name="description" content="Request a free vegetation management quote. Serving BC. Call (250) 492-0296 or submit our form." />
        <link rel="canonical" href="https://www.weedmanager.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Boultbee Vegetation Management" />
        <meta property="og:title" content="Contact Boultbee | Get a Free Quote | BC-Wide" />
        <meta property="og:description" content="Request a free vegetation management quote. Serving BC. Call (250) 492-0296 or submit our form." />
        <meta property="og:url" content="https://www.weedmanager.com/contact" />
        <meta property="og:image" content="https://www.weedmanager.com/images/og-contact.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Boultbee | Get a Free Quote | BC-Wide" />
        <meta name="twitter:description" content="Request a free vegetation management quote. Serving BC. Call (250) 492-0296 or submit our form." />
        <meta name="twitter:image" content="https://www.weedmanager.com/images/og-contact.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.weedmanager.com/" },
              { "@type": "ListItem", "position": 2, "name": "Contact", "item": "https://www.weedmanager.com/contact" }
            ]
          })}
        </script>
      </Helmet>
      <div className='bg-custom-earth'>
        <div className="wood-container">
          <div className="min-h-screen flex flex-col">

            {/* Hero Banner */}
            <div
              className="page-hero"
              style={{ backgroundImage: `url(${contactHero})`, backgroundPosition: 'center 35%' }}
            >
              <div className="page-hero-overlay" />
              <div className="relative z-10 text-center px-4">
                <h1 className="text-3xl md:text-5xl font-bold text-white mb-4" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>Get in Touch</h1>
                <p className="text-white/90 text-lg max-w-xl mx-auto" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>Request a free site assessment or ask about our vegetation management services.</p>
              </div>
            </div>

            {/* Two-column layout: Form + Sidebar */}
            <div className="flex flex-col lg:flex-row gap-6 w-[95%] mx-auto mb-8 mt-6">

              {/* Left: Contact Form */}
              <AnimateOnScroll animation="fade-in-up" delay={100} className="lg:w-3/5">
                <ContactForm />
              </AnimateOnScroll>

              {/* Right: Sidebar */}
              <AnimateOnScroll animation="fade-in-up" delay={200} className="lg:w-2/5">
                <div className="contact-text-container-blur h-full">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Other Ways to Reach Us</h3>

                  {/* Phone */}
                  <div className="flex items-start gap-3 mb-5">
                    <svg className="w-5 h-5 mt-0.5 text-green-700 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                    </svg>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Phone</p>
                      <a href="tel:1-250-492-0296" className="text-green-700 font-bold hover:underline">(250) 492-0296</a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-3 mb-5">
                    <svg className="w-5 h-5 mt-0.5 text-green-700 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <path d="M22 7l-10 7L2 7" />
                    </svg>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Email</p>
                      <a href="mailto:steve@weedmanager.com" className="text-green-700 font-bold hover:underline">steve@weedmanager.com</a>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex items-start gap-3 mb-5">
                    <svg className="w-5 h-5 mt-0.5 text-green-700 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 6v6l4 2" />
                    </svg>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Business Hours</p>
                      <p className="text-gray-700">Monday - Friday</p>
                      <p className="text-gray-700">7:00 AM - 5:00 PM PST</p>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="flex items-start gap-3 mb-6">
                    <svg className="w-5 h-5 mt-0.5 text-green-700 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Location</p>
                      <p className="text-gray-700">1101 Spiller Road</p>
                      <p className="text-gray-700">Penticton, BC V2A 6J9</p>
                    </div>
                  </div>

                  {/* Certification Logos */}
                  <div className="border-t border-gray-300 pt-4 mt-2">
                    <p className="text-sm font-semibold text-gray-700 mb-3 text-center">Certified & Compliant</p>
                    <div className="flex flex-wrap gap-4 items-center justify-center">
                      <img src={avettalogo} alt="Avetta Certified" className="h-8 w-auto" loading="lazy" width={400} height={399} />
                      <img src={ivmalogo} alt="IVMA of BC Member" className="h-8 w-auto" loading="lazy" width={248} height={117} />
                      <img src={worksafebclogo} alt="WorkSafeBC Compliant" className="h-8 w-auto" loading="lazy" width={843} height={841} />
                      <img src={isclogo} alt="ISC of BC" className="h-8 w-auto" loading="lazy" width={225} height={155} />
                    </div>
                  </div>
                </div>
              </AnimateOnScroll>

            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default ContactPage;
